import { inject } from "aurelia";
import { IHttpClient } from "@aurelia/fetch-client";
import { SessionStorage } from "./storage";
import * as env from "../../config/environment.json";

const absoluteUrl = /^([a-z][a-z0-9+\-.]*:)?\/\//i;

// The fetch client joins its base url by plain concatenation, which turns
// "/v1/x" into "api//v1/x". The services write their paths both ways.
function apiUrl(url) {
  if (absoluteUrl.test(url)) return url;
  return `${env.apiUrl.replace(/\/+$/, "")}/${url.replace(/^\/+/, "")}`;
}

// filenameFrom reads filename="…" from a Content-Disposition header, or
// returns null when there is none.
function filenameFrom(contentDisposition) {
  const match = /filename="([^"]+)"/.exec(contentDisposition ?? "");
  return match ? match[1] : null;
}

// A request resolves with { statusCode, statusText, isSuccess, response } where
// response is the body as text. A status of 400 or above rejects with the same
// message. A request that gets no response rejects with statusCode 0.
@inject(IHttpClient, SessionStorage)
export class ApiClient {
  constructor(http, storage) {
    this.http = http;
    http.configure((config) =>
      config
        .withDefaults({ headers: { Accept: "application/json", "x-encode": "base64" } })
        .withInterceptor({
          request(request) {
            const token = storage.getItem("token");
            if (token) {
              request.headers.set("Authorization", `Bearer ${token}`);
            }
            return request;
          },
        })
    );
  }

  get(url) {
    return this.send("GET", url);
  }

  post(url, content) {
    return this.send("POST", url, content);
  }

  put(url, content) {
    return this.send("PUT", url, content);
  }

  delete(url) {
    return this.send("DELETE", url);
  }

  async send(method, url, content) {
    const init = { method };
    if (content !== undefined && content !== null) {
      init.body = JSON.stringify(content);
      init.headers = { "Content-Type": "application/json" };
    }

    let response;
    try {
      response = await this.http.fetch(apiUrl(url), init);
    } catch (cause) {
      throw { statusCode: 0, statusText: "", isSuccess: false, response: "", cause };
    }

    const message = {
      statusCode: response.status,
      statusText: response.statusText,
      isSuccess: response.status >= 200 && response.status < 400,
      response: await response.text(),
    };
    if (!message.isSuccess) {
      throw message;
    }
    return message;
  }

  // getBlob fetches a binary response as { blob, filename }. It rejects like
  // send() does, with the body read as text so that error messages still
  // come through.
  async getBlob(url) {
    let response;
    try {
      response = await this.http.fetch(apiUrl(url), { method: "GET", headers: { Accept: "application/pdf" } });
    } catch (cause) {
      throw { statusCode: 0, statusText: "", isSuccess: false, response: "", cause };
    }

    if (response.status >= 400) {
      throw { statusCode: response.status, statusText: response.statusText, isSuccess: false, response: await response.text() };
    }
    return { blob: await response.blob(), filename: filenameFrom(response.headers.get("Content-Disposition")) };
  }
}
